import { useEffect, useState } from 'react';
import { api } from '../../api/client';
import { naviguer } from '../../App';
import type { Atelier, FakFeedback, FakKit } from '../../types';
import { JOURS_SEMAINE, fakBadgeClass, formatHoraire } from '../annuaire/format';
import { LIBELLES_STATUT_FAK, formatRelatif } from '../demande/format';

import { dePersonne } from '../messages/format';
/**
 * Extrait les thématiques du contenu libre d'une FRA (markdown léger : lignes
 * commençant par #, ## ou une puce -/*). Même heuristique que la validation
 * serveur « 3-6 thématiques » (server/routes/fak.ts) — les deux doivent
 * compter la même chose. Renvoie [] si le contenu ne se découpe pas ainsi :
 * l'appelant affiche alors le texte en paragraphe, jamais rien.
 */
export function thematiquesDepuisContenu(contenu: string | null | undefined): string[] {
  if (!contenu) return [];
  return contenu
    .split('\n')
    .filter((l) => /^\s*(#{1,3}\s|[-*]\s)/.test(l))
    .map((l) => l.replace(/^\s*(#{1,3}\s+|[-*]\s+)/, '').trim())
    .filter(Boolean);
}

/**
 * La FRA en aperçu par-dessus le contexte courant (bibliothèque,
 * demandes des collègues, urgence…) — on ne redirige jamais pour consulter
 * (feedback-ux-first) ; seul « Modifier la fiche » navigue, car l'édition
 * vit dans le panneau de l'atelier (AteliersPage).
 * `demandeId` : présent quand la fiche est ouverte depuis une demande — le
 * retour déposé est alors rattaché à ce remplacement précis.
 */
export function FicheFakOverlay({ atelierId, demandeId, onClose }: {
  atelierId: number; demandeId?: number; onClose: () => void;
}) {
  const [fak, setFak] = useState<FakKit | null>(null);
  const [peutModifier, setPeutModifier] = useState(false);
  const [atelier, setAtelier] = useState<Atelier | null>(null);
  const [feedback, setFeedback] = useState<FakFeedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [retourTexte, setRetourTexte] = useState('');
  const [retourEnvoi, setRetourEnvoi] = useState(false);
  const [retourMerci, setRetourMerci] = useState(false);

  useEffect(() => {
    const surEchap = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', surEchap);
    return () => document.removeEventListener('keydown', surEchap);
  }, [onClose]);

  useEffect(() => {
    let annule = false;
    (async () => {
      setLoading(true);
      setErreur(null);
      try {
        const [detail, a] = await Promise.all([api.getFak(atelierId), api.getAtelier(atelierId)]);
        if (annule) return;
        setFak(detail.fak);
        setPeutModifier(detail.peut_modifier);
        setAtelier(a);
        if (detail.fak) {
          const fb = await api.getFakFeedback(detail.fak.id);
          if (annule) return;
          setFeedback(fb);
        } else {
          setFeedback([]);
        }
      } catch (err) {
        if (!annule) setErreur(err instanceof Error ? err.message : 'Échec du chargement de la fiche');
      } finally {
        if (!annule) setLoading(false);
      }
    })();
    return () => { annule = true; };
  }, [atelierId]);

  const envoyerRetour = async () => {
    if (!fak || !retourTexte.trim()) return;
    setRetourEnvoi(true);
    setErreur(null);
    try {
      const cree = await api.addFakFeedback(fak.id, { contenu: retourTexte.trim(), demande_id: demandeId });
      setFeedback((prev) => [...prev, cree]);
      setRetourTexte('');
      setRetourMerci(true);
    } catch (err) {
      setErreur(err instanceof Error ? err.message : "Échec de l'envoi du retour");
    } finally {
      setRetourEnvoi(false);
    }
  };

  const themes = thematiquesDepuisContenu(fak?.contenu);
  const rienASaisir = !fak || (!fak.contenu && !fak.lien_document && !fak.echelle_difficulte);
  const sousTitre = atelier
    ? [
        atelier.lieu,
        atelier.jour_semaine != null ? `${JOURS_SEMAINE[atelier.jour_semaine]} ${formatHoraire(atelier.heure_debut, atelier.heure_fin)}`.trim() : null,
        atelier.titulaire_nom ? `fiche ${dePersonne(atelier.titulaire_nom)}` : null,
        fak?.updated_at ? `MAJ ${formatRelatif(fak.updated_at)}` : null,
      ].filter(Boolean).join(' · ')
    : '';

  return (
    // Pas de fermeture au clic sur l'overlay (règle commune aux modales de
    // l'app) : ✕ ou Échap uniquement — un clic à côté en lisant ne doit pas
    // faire disparaître la fiche.
    <div className="modal-overlay">
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">
            {atelier?.nom ?? '…'}
            {fak && <span className={`${fakBadgeClass(fak.statut)} ml-8`}>{LIBELLES_STATUT_FAK[fak.statut]}</span>}
          </h3>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {loading && <div className="spinner" />}
          {erreur && <div className="badge badge-err mb-12" style={{ display: 'block' }}>{erreur}</div>}
          {!loading && (
            rienASaisir ? (
              <p className="text-sm text-muted">Aucune FRA disponible pour cet atelier pour l'instant.</p>
            ) : (
              <>
                {sousTitre && <p className="text-xs text-ink-3" style={{ marginTop: 0 }}>{sousTitre}</p>}

                {themes.length > 0 ? (
                  <>
                    <h4 className="fak-fiche-h">Thématiques transmissibles</h4>
                    <ul className="fak-fiche-themes">
                      {themes.map((t, idx) => <li key={idx}>{t}</li>)}
                    </ul>
                  </>
                ) : fak?.contenu ? (
                  <p className="text-sm" style={{ whiteSpace: 'pre-wrap' }}>{fak.contenu}</p>
                ) : null}

                {fak?.echelle_difficulte && (
                  <>
                    <h4 className="fak-fiche-h">Échelle de difficulté</h4>
                    <p className="text-sm" style={{ margin: 0 }}>{fak.echelle_difficulte}</p>
                  </>
                )}

                {fak?.lien_document && (
                  <p style={{ marginBottom: 0 }}>
                    <a className="btn btn-sm" href={fak.lien_document} target="_blank" rel="noreferrer">Ouvrir le document complet</a>
                  </p>
                )}

                {fak && (
                  <>
                    <h4 className="fak-fiche-h">Retours de remplaçant·e·s ({feedback.length})</h4>
                    {feedback.length === 0 && <p className="text-sm text-muted" style={{ margin: 0 }}>Aucun retour pour l'instant.</p>}
                    {feedback.map((fb) => (
                      <div key={fb.id} className="fak-fiche-retour">
                        <div className="text-sm">{fb.contenu}</div>
                        <div className="text-xs text-ink-3">{fb.auteur_nom} · {formatRelatif(fb.created_at)}{fb.integre ? ' · intégré à la fiche' : ''}</div>
                      </div>
                    ))}
                    {retourMerci ? (
                      <p className="text-sm" style={{ color: 'var(--ok)', marginBottom: 0 }}>
                        Merci, votre retour est transmis — il aidera à améliorer la fiche.
                      </p>
                    ) : (
                      <div className="mt-8">
                        <div className="form-group">
                          <label className="form-label" htmlFor="fak-overlay-retour">Ajouter un retour</label>
                          <textarea
                            id="fak-overlay-retour" className="form-textarea" rows={2}
                            placeholder="Deux phrases suffisent — ce qui a aidé, ce qui manquait…"
                            value={retourTexte} onChange={(e) => setRetourTexte(e.target.value)}
                          />
                        </div>
                        <button
                          type="button" className="btn btn-sm btn-accent"
                          disabled={retourEnvoi || !retourTexte.trim()} onClick={envoyerRetour}
                        >
                          {retourEnvoi ? 'Envoi…' : 'Envoyer'}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </>
            )
          )}
        </div>
        {peutModifier && !loading && (
          <div className="modal-footer">
            <button
              type="button" className="btn btn-sm"
              onClick={() => { onClose(); naviguer('ateliers', undefined, { atelier: String(atelierId) }); }}
            >
              Modifier la fiche
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
